import { KeyBinding } from '../../shared/CharacterTypes';
import { DEFAULT_KEY_BINDINGS } from '../../shared/CharacterClasses';
import { storage } from './StorageService';
import { gameLogger } from '../../utils/GameLogger';
import { bindingKeyId, migrateKeyBindings, type StoredKeyBindings } from './keyBindingMigration';

export class KeyBindingManager {
  private static instance: KeyBindingManager;
  private bindings: KeyBinding[] = [];
  private defaultsSeen: string[] = DEFAULT_KEY_BINDINGS.bindings.map(bindingKeyId);
  private listeners: (() => void)[] = [];

  /**
   * Default bindings RETIRED by a version bump. The merge below only ever
   * ADDS missing defaults, so a stored copy of an old default would survive
   * forever — e.g. the pre-1.8 `basic_attack → 1` would keep firing the auto
   * attack on the same key the new `ability_slot_1 → 1` casts hotbar slot 1
   * (double-fire). Only EXACT action+key matches are dropped; a custom key
   * the user chose for the same action is untouched.
   */
  private static readonly RETIRED_DEFAULT_BINDINGS: ReadonlyArray<{ action: string; key: string }> = [
    // v1.8 (2026-06-11): key 1 / Numpad1 moved from basic_attack to hotbar
    // slot 1 (ability_slot_1). basic_attack relocated to F.
    { action: 'basic_attack', key: '1' },
    { action: 'basic_attack', key: 'NUMPAD_ONE' },
    // v2.2 (2026-08-08): retire the brief local-only v2.1 Character → N
    // default. Character stays on C; Talent Tree owns N.
    { action: 'character_sheet', key: 'N' },
    // v2.3 (2026-08-08): Quest Log moves from Q to L. The Twitch PiP is
    // player-disabled, freeing its former L binding.
    { action: 'quest_log', key: 'Q' },
    { action: 'twitch_pip', key: 'L' },
    // v2.6 (2026-10-08): M opens the World Map. The Map Picker's `map → M`
    // and the interim `world_map → O` (v2.5) retire; a player who put M on
    // another action keeps it (stored rows win getActionForKey).
    { action: 'map', key: 'M' },
    { action: 'world_map', key: 'O' },
  ];

  private constructor() {
    this.loadBindings();
  }

  public static getInstance(): KeyBindingManager {
    if (!KeyBindingManager.instance) {
      KeyBindingManager.instance = new KeyBindingManager();
    }
    return KeyBindingManager.instance;
  }

  /**
   * Load bindings from storage or use defaults. A stored copy from another
   * version (or from before `defaultsSeen`) goes through the migration.
   */
  private loadBindings(): void {
    try {
      if (typeof window === 'undefined') {
        this.bindings = [...DEFAULT_KEY_BINDINGS.bindings];
        return;
      }

      const config = storage.getObject<StoredKeyBindings>('helix_keybindings');
      if (config && Array.isArray(config.bindings)) {
        this.adopt(config);
      } else {
        this.bindings = [...DEFAULT_KEY_BINDINGS.bindings];
      }
    } catch (error) {
      gameLogger.error('input', 'Failed to load keybindings:', error);
      this.bindings = [...DEFAULT_KEY_BINDINGS.bindings];
    }
  }

  /** Take a stored or server config as the live bindings; returns whether it had to be migrated. */
  private adopt(config: StoredKeyBindings): boolean {
    const current = config.version === DEFAULT_KEY_BINDINGS.version && Array.isArray(config.defaultsSeen);
    if (current) {
      this.bindings = config.bindings;
      this.defaultsSeen = config.defaultsSeen!;
      return false;
    }
    gameLogger.info('input', `[KeyBindingManager] Migrating bindings (${config.version} -> ${DEFAULT_KEY_BINDINGS.version})`);
    const migrated = migrateKeyBindings(config, DEFAULT_KEY_BINDINGS, KeyBindingManager.RETIRED_DEFAULT_BINDINGS);
    this.bindings = migrated.bindings;
    this.defaultsSeen = migrated.defaultsSeen ?? this.defaultsSeen;
    this.saveBindings();
    return true;
  }

  /**
   * Save current bindings to storage
   */
  public saveBindings(): void {
    try {
      storage.setObject('helix_keybindings', this.getConfig());
      this.notifyListeners();
    } catch (error) {
      gameLogger.error('input', 'Failed to save keybindings:', error);
    }
  }

  /**
   * Take the character's saved binds from the server (modular bars phase 3) as the
   * live bindings and the browser cache. Returns whether they had to be migrated,
   * in which case the caller sends the migrated copy back.
   */
  public applyServerConfig(config: StoredKeyBindings): boolean {
    const migrated = this.adopt(config);
    if (!migrated) this.saveBindings();
    return migrated;
  }

  /** The live bindings as a storable config. */
  public getConfig(): StoredKeyBindings {
    return { version: DEFAULT_KEY_BINDINGS.version, bindings: this.bindings, defaultsSeen: this.defaultsSeen };
  }

  /**
   * Get all bindings
   */
  public getBindings(): KeyBinding[] {
    return this.bindings;
  }

  /**
   * Get key for a specific action
   */
  public getKeyForAction(action: string): string | undefined {
    return this.bindings.find(b => b.action === action)?.key;
  }

  /**
   * Get action for a specific key
   */
  public getActionForKey(key: string): string | undefined {
    // Case insensitive comparison
    const normalizedKey = key.toUpperCase();
    return this.bindings.find(b => b.key.toUpperCase() === normalizedKey)?.action;
  }

  /**
   * Update a binding
   */
  public updateBinding(action: string, newKey: string): void {
    const index = this.bindings.findIndex(b => b.action === action);
    if (index !== -1) {
      this.bindings[index] = { ...this.bindings[index], key: newKey.toUpperCase() };
      this.saveBindings();
    }
  }

  /**
   * Reset to defaults
   */
  public resetToDefaults(): void {
    this.bindings = [...DEFAULT_KEY_BINDINGS.bindings];
    this.defaultsSeen = DEFAULT_KEY_BINDINGS.bindings.map(bindingKeyId);
    this.saveBindings();
  }

  /**
   * Subscribe to changes
   */
  public subscribe(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(l => l());
  }
}
